// ДАННЫЕ СТАТЬИ СПРАВКИ В КАБИНЕТЕ — ОДНИ КЛЮЧИ И ОДИН ПРОГРЕВ (ТЗ HELP-ARTICLE-UNIFY §5.4, владелец 25.10.2026:
// «вся справка загружена заранее везде»). Оглавление вместе с телами ВСЕХ статей — один ключ общего кэша
// (usePersistentData); картинки статей (снимки шагов) — в кэше браузера. Что и когда греть — план прогрева
// (`lib/app-pages/warm-plan.ts`: `help.index` — У1 на «Аккаунте», `help.images.*` — снимки ОТКРЫТОЙ статьи и одной
// следующей, SPEED §6.3); очередь — lib/warm.ts, оглавление ставит components/cabinet/warm-cache.tsx.
// ОГЛАВЛЕНИЕ ХРАНИТСЯ С ВЕРСИЕЙ САЙТА (ТЗ HELP-WARM-FIX §1 п.6): справка меняется только с выкаткой, поэтому данные той же
// версии (`HELP_VERSION`) верны без проверки сервера; новая выкатка — одна фоновая проверка.
import { articleTitles, type HelpTitles } from '@/lib/help-titles';
import type { HelpBlock } from '@/lib/help-markdown';
import { peekPersistent } from '@/lib/use-persistent-data';
import { HELP_FIRST_IMAGES } from '@/lib/app-pages/warm-plan';
import { heldImages } from '@/lib/scene-assets';
import { warmTargetRun } from '@/lib/warm';

export interface HelpCard {
  section: string;
  slug: string;
  title: string;
  heading?: string;
  short?: string;
  description: string;
  webOnly: boolean;
}
export interface HelpArticleData extends HelpCard {
  blocks: HelpBlock[];
}
/** Оглавление справки — разделы и статьи С ТЕЛАМИ (GET /api/help?locale=…): один ключ на всю справку. */
export interface HelpIndexReply {
  ok?: boolean;
  sections?: { slug: string; title: string; description: string; articles: HelpArticleData[] }[];
}

export const helpIndexKey = (locale: string): string => `help:index:${locale}`;
export const helpIndexUrl = (locale: string): string => `/api/help?locale=${locale}`;
/** Версия сайта, с которой сохраняется оглавление (`usePersistentData` `version`). */
export const HELP_VERSION: string = process.env.NEXT_PUBLIC_BUILD_ID ?? '';
/** Опции хука/ожидания для ключа оглавления — одни у всех мест. */
export const helpIndexOpts = HELP_VERSION ? { version: HELP_VERSION } : {};

/** Статья из оглавления (тела статей приходят вместе с ним). */
export function articleOf(index: HelpIndexReply | null | undefined, section: string, slug: string): HelpArticleData | null {
  const a = index?.sections?.flatMap((s) => s.articles ?? []).find((x) => x.section === section && x.slug === slug);
  // Копия оглавления прежней сборки (без тел) статьёй не считается.
  return a && Array.isArray(a.blocks) ? a : null;
}

/** Заголовки статьи из кэша оглавления — окно открывают с готовой шапкой. */
export function cardTitles(locale: string, section: string, slug: string): HelpTitles | null {
  const art = articleOf(peekPersistent<HelpIndexReply>(helpIndexKey(locale)), section, slug);
  return art ? articleTitles(art) : null;
}

// ── Картинки статей справки (help.images.*, ТЗ ~/projects/SPEED-TZ.md §6.3) ──
/** Загрузить картинки в кэш браузера; промис — когда все встали или упали. */
function loadImages(srcs: readonly string[]): Promise<void> {
  return Promise.all(
    srcs.map(
      (src) =>
        new Promise<void>((done) => {
          const im = new Image();
          heldImages.push(im); // живой объект — <img> статьи берёт картинку из памяти сразу (scene-assets.ts)
          im.decoding = 'async';
          im.onload = () => done();
          im.onerror = () => done();
          im.src = src;
        }),
    ),
  ).then(() => undefined);
}
/** Первые HELP_FIRST_IMAGES снимков статьи (то, что видно при открытии). */
function firstImages(blocks: readonly { k: string; src?: string }[] | undefined): string[] {
  return (blocks ?? [])
    .filter((b) => b.k === 'img' && !!b.src)
    .map((b) => b.src as string)
    .slice(0, HELP_FIRST_IMAGES);
}

/**
 * СТАТЬЯ СПРАВКИ ОТКРЫТА (зовёт окно статьи, components/help/help-article.tsx — SPEED §6.3): её первые снимки — цель
 * `help.images.first` (У1; в разметке они и так `fetchpriority="high"`, остальные — `loading="lazy"`), и ОДНА следующая
 * статья по оглавлению — `help.images.rest` (У3, в простой). Прочие статьи заранее не греются.
 */
export function warmHelpArticle(locale: string, section: string, slug: string): void {
  const all = peekPersistent<HelpIndexReply>(helpIndexKey(locale))?.sections?.flatMap((s) => s.articles ?? []) ?? [];
  const at = all.findIndex((a) => a.section === section && a.slug === slug);
  if (at < 0) return;
  const here = firstImages(all[at]!.blocks);
  if (here.length) warmTargetRun('help.images.first', `help-img:${section}/${slug}`, () => loadImages(here));
  const next = all[at + 1];
  const ahead = next ? firstImages(next.blocks) : [];
  if (next && ahead.length) warmTargetRun('help.images.rest', `help-img:${next.section}/${next.slug}`, () => loadImages(ahead));
}
